import { useState, useEffect } from "react"
import { X, Mail, Phone, MessageSquare, Edit, Trash2, Calendar, FileText, Activity, User, Building, CheckSquare } from "lucide-react"
import api from "../../api/axios"
import { ContactFilesTab } from "./ContactFilesTab"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"
import { Label } from "../ui/label"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip"
import { getTagChipStyles } from "../../utils/tagHelpers"
import { useTheme } from "../../contexts/ThemeContext"
import { useOptionalResourceSubscription } from "../../contexts/NotificationsContext"

export interface Contact {
  id: string
  contact_type: "individual" | "company"

  // Campi calcolati per UI lista
  name: string
  status: "active" | "inactive"
  isFavorite?: boolean

  // Telefoni
  mobile_phone: string
  phone_2?: string | null
  phone_3?: string | null

  // Email
  email?: string | null
  email_2?: string | null
  email_3?: string | null
  pec_email?: string | null

  // Persona fisica
  first_name?: string | null
  last_name?: string | null
  fiscal_code?: string | null
  address?: string | null
  role?: string | null
  company_reference?: string | null

  // Persona giuridica
  company_name?: string | null
  vat_number?: string | null
  company_fiscal_code?: string | null
  legal_address?: string | null
  operational_address?: string | null
  legal_representative_first_name?: string | null
  legal_representative_last_name?: string | null

  // Altri
  notes?: string | null
  websites?: string[]
  related_contacts?: Array<{ id: number; name: string; contact_type: string }>
  avatar_url?: string | null

  // Campi UI legacy (mantieni per compatibilità con lista)
  tags: {id: number, name: string, color: string}[]
  hasPhone: boolean
  hasEmail: boolean
  type: "person" | "company"
}

export interface Task {
  id: string
  title: string
  description?: string
  priority: "high" | "medium" | "low"
  dueDate?: string
  status: "todo" | "in_progress" | "review" | "completed"
  linked_contacts?: string[]
  assigned_to?: string
}

interface ContactDetailPanelProps {
  contact: Contact
  onClose: () => void
  onUpdateContact?: (contact: Contact) => void
  onDeleteContact?: (contactId: string) => void
  onOpenEditModal?: () => void
  tasks?: Task[]
  onOpenTaskModal?: (task: Task) => void
}

export function ContactDetailPanel({
  contact,
  onClose,
  onUpdateContact,
  onDeleteContact,
  onOpenEditModal,
  tasks = [],
  onOpenTaskModal,
}: ContactDetailPanelProps) {
  const { theme } = useTheme()
  const [isEditMode, setIsEditMode] = useState(false)
  const [showEmailDialog, setShowEmailDialog] = useState(false)
  const [showCallDialog, setShowCallDialog] = useState(false)
  const [showMessageDialog, setShowMessageDialog] = useState(false)
  const [emailContent, setEmailContent] = useState("")
  const [messageContent, setMessageContent] = useState("")

  // Activities state (tasks and appointments)
  interface ContactTask {
    id: number
    title: string
    status_detail?: { key: string; name: string; color?: string }
    priority: number
    due_date?: string
    is_overdue?: boolean
    is_completed?: boolean
    project_detail?: { name: string } | null
  }
  interface ContactAppointment {
    id: number
    title: string
    appointment_type: string
    date: string
    start_time: string
    end_time: string
  }
  const [contactTasks, setContactTasks] = useState<ContactTask[]>([])
  const [contactAppointments, setContactAppointments] = useState<ContactAppointment[]>([])
  const [loadingTasks, setLoadingTasks] = useState(true)
  const [loadingAppointments, setLoadingAppointments] = useState(true)
  const [tasksError, setTasksError] = useState(false)
  const [appointmentsError, setAppointmentsError] = useState(false)
  const [activitiesVersion, setActivitiesVersion] = useState(0)
  useOptionalResourceSubscription('task', () => setActivitiesVersion(value => value + 1))
  useOptionalResourceSubscription('appointment', () => setActivitiesVersion(value => value + 1))

  // Load tasks and appointments on mount
  useEffect(() => {
    let cancelled = false
    setLoadingTasks(true)
    setLoadingAppointments(true)
    setTasksError(false)
    setAppointmentsError(false)
    setContactTasks([])
    setContactAppointments([])

    const loadTasks = async () => {
      const tasks: ContactTask[] = []
      let hasMore = true
      while (hasMore && !cancelled) {
        const response = await api.get(`api/tasks/tasks/?related_contacts=${encodeURIComponent(contact.id)}&limit=100&offset=${tasks.length}&ordering=id`)
        const rows = Array.isArray(response.data) ? response.data : response.data?.results || []
        tasks.push(...rows)
        hasMore = !Array.isArray(response.data) && !!response.data?.next && rows.length > 0
      }
      return tasks
    }
    loadTasks()
      .then(rows => { if (!cancelled) setContactTasks(rows) })
      .catch(() => { if (!cancelled) setTasksError(true) })
      .finally(() => { if (!cancelled) setLoadingTasks(false) })
    api.get(`api/appointments/appointments/?client=${encodeURIComponent(contact.id)}`)
      .then(response => { if (!cancelled) setContactAppointments(Array.isArray(response.data) ? response.data : response.data?.results || []) })
      .catch(() => { if (!cancelled) setAppointmentsError(true) })
      .finally(() => { if (!cancelled) setLoadingAppointments(false) })
    return () => { cancelled = true }
  }, [contact.id, activitiesVersion])

  // Generate initials
  const initials = contact.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  const handleSendEmail = () => {
    console.log("[v0] Email sent to:", contact.name, "Content:", emailContent)
    setShowEmailDialog(false)
    setEmailContent("")
  }

  const handleMakeCall = () => {
    console.log("[v0] Call made to:", contact.name)
    setShowCallDialog(false)
  }

  const handleSendMessage = () => {
    console.log("[v0] Message sent to:", contact.name, "Content:", messageContent)
    setShowMessageDialog(false)
    setMessageContent("")
  }


  return (
    <>
      <div className="contact-detail flex min-w-0 min-h-0 flex-col h-full bg-[var(--hexa-surface-elevated)]">
        {/* Header */}
        <div className="shrink-0 max-h-[45dvh] overflow-y-auto border-b border-[var(--hexa-border)] px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-[var(--hexa-text-primary)]">Dettagli</h2>
            <Button variant="ghost" size="icon" aria-label="Chiudi dettaglio contatto" onClick={onClose} className="shrink-0 text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)]">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Contact Header */}
          <div className="flex items-start gap-3 mb-3">
            <div className="relative">
              <Avatar className="h-12 w-12 bg-[var(--hexa-border)]">
                {contact.avatar_url && <AvatarImage src={contact.avatar_url} alt={contact.name} />}
                <AvatarFallback className="bg-[var(--hexa-border)] text-[var(--hexa-text-primary)] font-semibold text-sm">
                  {initials}
                </AvatarFallback>
              </Avatar>
              {contact.status === "active" && (
                <div className="absolute -bottom-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-[var(--hexa-surface-elevated)]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold mb-0.5 text-[var(--hexa-text-primary)]">{contact.name}</h3>
              {(contact.role || contact.company_reference || contact.company_name) && (
                <p className="text-xs text-[var(--hexa-text-secondary)] mb-1.5">
                  {[contact.role, contact.company_reference || contact.company_name].filter(Boolean).join(" • ")}
                </p>
              )}
              <div className="flex gap-2 flex-wrap">
                <Badge variant="outline" className="text-xs bg-[var(--hexa-border-subtle)] border-[var(--hexa-border)] text-[var(--hexa-text-primary)]">
                  {contact.type === "person" ? "Persona" : "Azienda"}
                </Badge>
                {contact.tags.slice(0, 3).map((tag) => (
                  <Badge key={tag.id} variant="secondary" className="max-w-full whitespace-normal break-words text-left text-xs border"
                    style={getTagChipStyles(tag.color, theme)}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <TooltipProvider>
            <div className="flex gap-1.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled
                      className="w-full bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-muted)] cursor-not-allowed opacity-60"
                    >
                      <Mail className="w-3.5 h-3.5 mr-1.5" />
                      Invia Email
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Funzione in arrivo</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled
                      className="w-full bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-muted)] cursor-not-allowed opacity-60"
                    >
                      <Phone className="w-3.5 h-3.5 mr-1.5" />
                      Chiama
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Funzione in arrivo</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex-1">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled
                      className="w-full bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)] text-[var(--hexa-text-muted)] cursor-not-allowed opacity-60"
                    >
                      <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
                      Messaggio
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Funzione in arrivo</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>

        {/* Tabs Content - Scrollable */}
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto scrollbar-hexa">
          <Tabs defaultValue="info" className="w-full">
            <TabsList className="w-full justify-start border-b border-[var(--hexa-border)] rounded-none h-9 px-4 bg-transparent">
              <TabsTrigger value="info" className="gap-2 data-[state=active]:bg-[var(--hexa-border-subtle)] data-[state=active]:!text-[var(--hexa-text-muted)] !text-[var(--hexa-text-muted)]">
                <FileText className="w-3.5 h-3.5" />
                Informazioni
              </TabsTrigger>
              <TabsTrigger value="activity" className="gap-2 data-[state=active]:bg-[var(--hexa-border-subtle)] data-[state=active]:!text-[var(--hexa-text-muted)] !text-[var(--hexa-text-muted)]">
                <Activity className="w-3.5 h-3.5" />
                Attività
              </TabsTrigger>
              <TabsTrigger value="files" className="gap-2 data-[state=active]:bg-[var(--hexa-border-subtle)] data-[state=active]:!text-[var(--hexa-text-muted)] !text-[var(--hexa-text-muted)]">
                <FileText className="w-3.5 h-3.5" />
                File
              </TabsTrigger>
            </TabsList>

            <div className="p-4">
              <TabsContent value="info" className="mt-0 space-y-2.5">
                {!!contact.websites?.length && <div className="rounded-lg border border-[var(--hexa-border)] bg-[var(--hexa-button-cancel)] p-3">
                  <h4 className="mb-2 text-xs font-medium text-[var(--hexa-purple)]">Siti web</h4>
                  <ul className="space-y-1">{contact.websites.map((url, index) => <li key={index}>
                    <a href={/^https?:\/\//i.test(url) ? url : undefined} target="_blank" rel="noopener noreferrer" className="text-xs break-all text-[var(--hexa-text-primary)] underline">{url}</a>
                  </li>)}</ul>
                </div>}
                {/* CARD 1 — Contatti */}
                {(contact.email || contact.mobile_phone || contact.pec_email) && (
                  <div className="bg-[var(--hexa-button-cancel)] border border-[var(--hexa-border)] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2.5">
                      <Mail className="w-3.5 h-3.5 text-[var(--hexa-purple)]" />
                      <span className="text-[11px] font-medium text-[var(--hexa-purple)]">Contatti</span>
                    </div>

                    {/* Email */}
                    {(contact.email || contact.email_2 || contact.email_3) && (
                      <>
                        <div className="mb-2">
                          <Label className="text-[10px] text-[var(--hexa-text-muted)]">Email</Label>
                          <p className="text-xs text-[var(--hexa-text-primary)]">{contact.email}</p>
                          {contact.email_2 && (
                            <p className="text-xs text-[var(--hexa-text-muted)]">{contact.email_2}</p>
                          )}
                          {contact.email_3 && (
                            <p className="text-xs text-[var(--hexa-text-muted)]">{contact.email_3}</p>
                          )}
                        </div>
                        {(contact.mobile_phone || contact.pec_email) && (
                          <div className="h-px bg-[var(--hexa-border)] my-2" />
                        )}
                      </>
                    )}

                    {/* Telefono */}
                    {(contact.mobile_phone || contact.phone_2 || contact.phone_3) && (
                      <>
                        <div className="mb-2">
                          <Label className="text-[10px] text-[var(--hexa-text-muted)]">Telefono</Label>
                          <p className="text-xs text-[var(--hexa-text-primary)]">{contact.mobile_phone}</p>
                          {contact.phone_2 && (
                            <p className="text-xs text-[var(--hexa-text-muted)]">{contact.phone_2}</p>
                          )}
                          {contact.phone_3 && (
                            <p className="text-xs text-[var(--hexa-text-muted)]">{contact.phone_3}</p>
                          )}
                        </div>
                        {contact.pec_email && (
                          <div className="h-px bg-[var(--hexa-border)] my-2" />
                        )}
                      </>
                    )}

                    {/* PEC */}
                    {contact.pec_email && (
                      <div>
                        <Label className="text-[10px] text-[var(--hexa-text-muted)]">PEC</Label>
                        <p className="text-xs text-[var(--hexa-text-primary)]">{contact.pec_email}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* CARD 2 — Informazioni (persona) / Informazioni aziendali (azienda) */}
                {contact.contact_type === "individual" ? (
                  // PERSONA FISICA
                  (contact.company_reference || contact.role || contact.fiscal_code || contact.address || (contact.related_contacts && contact.related_contacts.length > 0)) && (
                    <div className="bg-[var(--hexa-button-cancel)] border border-[var(--hexa-border)] rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2.5">
                        <User className="w-3.5 h-3.5 text-[var(--hexa-purple)]" />
                        <span className="text-[11px] font-medium text-[var(--hexa-purple)]">Informazioni</span>
                      </div>

                      {/* Azienda | Ruolo */}
                      {(contact.company_reference || contact.role) && (
                        <>
                          <div className="contact-info-pair flex gap-4">
                            {contact.company_reference && (
                              <div className="flex-1">
                                <Label className="text-[10px] text-[var(--hexa-text-muted)]">Azienda</Label>
                                <p className="text-xs text-[var(--hexa-text-primary)]">{contact.company_reference}</p>
                              </div>
                            )}
                            {contact.role && (
                              <div className="flex-1">
                                <Label className="text-[10px] text-[var(--hexa-text-muted)]">Ruolo</Label>
                                <p className="text-xs text-[var(--hexa-text-primary)]">{contact.role}</p>
                              </div>
                            )}
                          </div>
                          {(contact.fiscal_code || contact.address || (contact.related_contacts && contact.related_contacts.length > 0)) && (
                            <div className="h-px bg-[var(--hexa-border)] my-2" />
                          )}
                        </>
                      )}

                      {/* Codice fiscale | Indirizzo */}
                      {(contact.fiscal_code || contact.address) && (
                        <>
                          <div className="contact-info-pair flex gap-4">
                            {contact.fiscal_code && (
                              <div className="flex-1">
                                <Label className="text-[10px] text-[var(--hexa-text-muted)]">Codice fiscale</Label>
                                <p className="text-xs text-[var(--hexa-text-primary)]">{contact.fiscal_code}</p>
                              </div>
                            )}
                            {contact.address && (
                              <div className="flex-1">
                                <Label className="text-[10px] text-[var(--hexa-text-muted)]">Indirizzo</Label>
                                <p className="text-xs text-[var(--hexa-text-primary)]">{contact.address}</p>
                              </div>
                            )}
                          </div>
                          {contact.related_contacts && contact.related_contacts.length > 0 && (
                            <div className="h-px bg-[var(--hexa-border)] my-2" />
                          )}
                        </>
                      )}

                      {/* Contatti collegati */}
                      {contact.related_contacts && contact.related_contacts.length > 0 && (
                        <div>
                          <Label className="text-[10px] text-[var(--hexa-text-muted)]">Contatti collegati</Label>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {contact.related_contacts.map((rc) => (
                              <span
                                key={rc.id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[var(--hexa-surface)] border border-[var(--hexa-border)] text-[11px] text-[var(--hexa-text-primary)]"
                              >
                                {rc.contact_type === "company" ? (
                                  <Building className="w-3 h-3 text-[var(--hexa-text-muted)]" />
                                ) : (
                                  <User className="w-3 h-3 text-[var(--hexa-text-muted)]" />
                                )}
                                {rc.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  // PERSONA GIURIDICA
                  (contact.vat_number || contact.company_fiscal_code || contact.legal_address || contact.operational_address || contact.legal_representative_first_name || (contact.related_contacts && contact.related_contacts.length > 0)) && (
                    <div className="bg-[var(--hexa-button-cancel)] border border-[var(--hexa-border)] rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2.5">
                        <Building className="w-3.5 h-3.5 text-[var(--hexa-purple)]" />
                        <span className="text-[11px] font-medium text-[var(--hexa-purple)]">Informazioni aziendali</span>
                      </div>

                      {/* P.IVA | Codice fiscale */}
                      {(contact.vat_number || contact.company_fiscal_code) && (
                        <>
                          <div className="contact-info-pair flex gap-4">
                            {contact.vat_number && (
                              <div className="flex-1">
                                <Label className="text-[10px] text-[var(--hexa-text-muted)]">P.IVA</Label>
                                <p className="text-xs text-[var(--hexa-text-primary)]">{contact.vat_number}</p>
                              </div>
                            )}
                            {contact.company_fiscal_code && (
                              <div className="flex-1">
                                <Label className="text-[10px] text-[var(--hexa-text-muted)]">Codice fiscale</Label>
                                <p className="text-xs text-[var(--hexa-text-primary)]">{contact.company_fiscal_code}</p>
                              </div>
                            )}
                          </div>
                          {(contact.legal_address || contact.operational_address || contact.legal_representative_first_name || (contact.related_contacts && contact.related_contacts.length > 0)) && (
                            <div className="h-px bg-[var(--hexa-border)] my-2" />
                          )}
                        </>
                      )}

                      {/* Sede legale | Sede operativa */}
                      {(contact.legal_address || contact.operational_address) && (
                        <>
                          <div className="contact-info-pair flex gap-4">
                            {contact.legal_address && (
                              <div className="flex-1">
                                <Label className="text-[10px] text-[var(--hexa-text-muted)]">Sede legale</Label>
                                <p className="text-xs text-[var(--hexa-text-primary)]">{contact.legal_address}</p>
                              </div>
                            )}
                            {contact.operational_address && (
                              <div className="flex-1">
                                <Label className="text-[10px] text-[var(--hexa-text-muted)]">Sede operativa</Label>
                                <p className="text-xs text-[var(--hexa-text-primary)]">{contact.operational_address}</p>
                              </div>
                            )}
                          </div>
                          {(contact.legal_representative_first_name || (contact.related_contacts && contact.related_contacts.length > 0)) && (
                            <div className="h-px bg-[var(--hexa-border)] my-2" />
                          )}
                        </>
                      )}

                      {/* Rappresentante legale */}
                      {(contact.legal_representative_first_name || contact.legal_representative_last_name) && (
                        <>
                          <div>
                            <Label className="text-[10px] text-[var(--hexa-text-muted)]">Rappresentante legale</Label>
                            <p className="text-xs text-[var(--hexa-text-primary)]">
                              {[contact.legal_representative_first_name, contact.legal_representative_last_name].filter(Boolean).join(" ")}
                            </p>
                          </div>
                          {contact.related_contacts && contact.related_contacts.length > 0 && (
                            <div className="h-px bg-[var(--hexa-border)] my-2" />
                          )}
                        </>
                      )}

                      {/* Contatti collegati */}
                      {contact.related_contacts && contact.related_contacts.length > 0 && (
                        <div>
                          <Label className="text-[10px] text-[var(--hexa-text-muted)]">Contatti collegati</Label>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {contact.related_contacts.map((rc) => (
                              <span
                                key={rc.id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[var(--hexa-surface)] border border-[var(--hexa-border)] text-[11px] text-[var(--hexa-text-primary)]"
                              >
                                {rc.contact_type === "company" ? (
                                  <Building className="w-3 h-3 text-[var(--hexa-text-muted)]" />
                                ) : (
                                  <User className="w-3 h-3 text-[var(--hexa-text-muted)]" />
                                )}
                                {rc.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}

                {/* CARD 3 — Note */}
                {contact.notes && (
                  <div className="bg-[var(--hexa-button-cancel)] border border-[var(--hexa-border)] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2.5">
                      <FileText className="w-3.5 h-3.5 text-[var(--hexa-purple)]" />
                      <span className="text-[11px] font-medium text-[var(--hexa-purple)]">Note</span>
                    </div>
                    <p className="text-[11px] text-[var(--hexa-text-secondary)] leading-relaxed whitespace-pre-wrap">
                      {contact.notes}
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="activity" className="mt-0">
                {tasksError || appointmentsError ? (
                  <div className="py-8 text-center">
                    {tasksError && <p role="alert" className="mb-3 text-xs">Impossibile caricare i task collegati.</p>}
                    {appointmentsError && <p role="alert" className="mb-3 text-xs">Impossibile caricare gli appuntamenti collegati.</p>}
                    <Button variant="outline" onClick={() => setActivitiesVersion(value => value + 1)}>Riprova attività</Button>
                  </div>
                ) : !loadingTasks && !loadingAppointments && contactTasks.length === 0 && contactAppointments.length === 0 ? (
                  <p className="text-xs text-[var(--hexa-text-muted)] text-center py-8">Nessuna attività collegata</p>
                ) : null}
                  <>
                    {loadingTasks && <p className="text-xs py-3">Caricamento task...</p>}
                    {loadingAppointments && <p className="text-xs py-3">Caricamento agenda...</p>}
                    {!loadingTasks && !tasksError && contactTasks.length === 0 && contactAppointments.length > 0 && (
                      <p className="text-xs py-3 text-[var(--hexa-text-muted)]">Nessun task collegato</p>
                    )}
                    {/* Task section */}
                    {contactTasks.length > 0 && (
                      <div>
                        <h4 className="text-[11px] font-medium text-[var(--hexa-text-secondary)] mb-1.5">Task ({contactTasks.length})</h4>
                        <div className="space-y-1">
                          {contactTasks.map((task) => {
                            const getPriorityBadge = (priority: number) => {
                              switch (priority) {
                                case 3:
                                  return { className: "bg-red-400/20 text-red-400", label: "Alta" }
                                case 2:
                                  return { className: "bg-amber-400/20 text-amber-400", label: "Media" }
                                case 1:
                                default:
                                  return { className: "bg-green-400/20 text-green-400", label: "Bassa" }
                              }
                            }
                            const priorityBadge = getPriorityBadge(task.priority)
                            const formatDate = (dateString?: string) => {
                              if (!dateString) return ""
                              const date = new Date(dateString)
                              return date.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })
                            }
                            return (
                              <div
                                key={task.id}
                                className="flex flex-wrap items-center gap-2 bg-[var(--hexa-button-cancel)] rounded-md px-2.5 py-2"
                              >
                                <CheckSquare className="w-3.5 h-3.5 text-[var(--hexa-text-muted)] flex-shrink-0" />
                                <span className="flex-1 min-w-0 break-words [overflow-wrap:anywhere] text-xs font-medium text-[var(--hexa-text-primary)]">
                                  {task.title}
                                </span>
                                <Badge className={`text-[10px] px-1.5 py-0 border-0 ${priorityBadge.className}`}>
                                  {priorityBadge.label}
                                </Badge>
                                <span className="text-[10px] text-[var(--hexa-text-secondary)]">
                                  {task.is_completed ? "Completato" : task.status_detail?.name || "Inbox"}
                                </span>
                                {task.due_date && (
                                  <span className={`text-[10px] flex-shrink-0 ${task.is_overdue ? "text-red-400" : "text-[var(--hexa-text-muted)]"}`}>
                                    {formatDate(task.due_date)}
                                  </span>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Agenda section */}
                    {contactAppointments.length > 0 && (
                      <div className={contactTasks.length > 0 ? "mt-3" : ""}>
                        <h4 className="text-[11px] font-medium text-[var(--hexa-text-secondary)] mb-1.5">Agenda</h4>
                        <div className="space-y-1">
                          {contactAppointments.map((appointment) => {
                            const getTypeBadge = (type: string) => {
                              switch (type) {
                                case "EVENT":
                                  return { className: "bg-purple-400/20 text-purple-400", label: "Evento" }
                                case "MEETING":
                                  return { className: "bg-blue-400/20 text-blue-400", label: "Meeting" }
                                case "APPOINTMENT":
                                default:
                                  return { className: "bg-cyan-400/20 text-cyan-400", label: "Appuntamento" }
                              }
                            }
                            const typeBadge = getTypeBadge(appointment.appointment_type)
                            const formatDateTime = (date: string, time: string) => {
                              const d = new Date(date)
                              const dateStr = d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })
                              const timeStr = time?.slice(0, 5) || ""
                              return `${dateStr} ${timeStr}`
                            }
                            return (
                              <div
                                key={appointment.id}
                                className="flex items-center gap-2 bg-[var(--hexa-button-cancel)] rounded-md px-2.5 py-1.5"
                              >
                                <Calendar className="w-3.5 h-3.5 text-[var(--hexa-text-muted)] flex-shrink-0" />
                                <span className="flex-1 text-xs font-medium text-[var(--hexa-text-primary)] truncate">
                                  {appointment.title}
                                </span>
                                <Badge className={`text-[10px] px-1.5 py-0 border-0 ${typeBadge.className}`}>
                                  {typeBadge.label}
                                </Badge>
                                <span className="text-[10px] text-[var(--hexa-text-muted)] flex-shrink-0">
                                  {formatDateTime(appointment.date, appointment.start_time)}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </>
              </TabsContent>

              <TabsContent value="files" className="mt-0">
                <ContactFilesTab contactId={contact.id} />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[var(--hexa-border)] px-4 py-2.5">
          <div className="flex gap-2">
            {/* Modifica */}
            <Button
              variant="ghost"
              onClick={() => onOpenEditModal?.()}
              className="flex-1 text-xs py-1.5 text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)] bg-[var(--hexa-button-cancel)] border border-[var(--hexa-border)]"
            >
              <Edit className="w-3 h-3 mr-1.5" />
              Modifica
            </Button>

            {/* Elimina */}
            <Button
              onClick={() => onDeleteContact?.(contact.id)}
              className="flex-1 text-xs py-1.5 bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white"
            >
              <Trash2 className="w-3 h-3 mr-1.5" />
              Elimina
            </Button>
          </div>
        </div>
      </div>

      {/* Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia Email a {contact.name}</DialogTitle>
            <DialogDescription>
              Componi il tuo messaggio email
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="email-subject">Oggetto</Label>
              <Input id="email-subject" placeholder="Oggetto email" className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)]" />
            </div>
            <div>
              <Label htmlFor="email-content">Messaggio</Label>
              <Textarea
                id="email-content"
                placeholder="Scrivi il tuo messaggio..."
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
                rows={5}
                className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSendEmail}>Invia Email</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Call Dialog */}
      <Dialog open={showCallDialog} onOpenChange={setShowCallDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Chiama {contact.name}</DialogTitle>
            <DialogDescription>
              Stai per effettuare una chiamata a {contact.mobile_phone || "Non disponibile"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCallDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleMakeCall}>Conferma Chiamata</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia Messaggio a {contact.name}</DialogTitle>
            <DialogDescription>
              Invia un messaggio diretto
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="message-content">Messaggio</Label>
              <Textarea
                id="message-content"
                placeholder="Scrivi il tuo messaggio..."
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                rows={5}
                className="bg-[var(--hexa-button-cancel)] border-[var(--hexa-border)]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSendMessage}>Invia Messaggio</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  )
}
